import React, { useContext, useEffect, useState } from "react"
import PropTypes from "prop-types"
import { Box, Paper, Grid, Typography, Button } from "@mui/material"
import i18n from "i18n"

import AuthContext from "context/auth/authContext"
import InvoiceTotals from "components/Invoices/Invoice/InvoiceTotals"
import InvoiceConceptRow from "components/Invoices/Invoice/InvoiceConceptRow"
import DiscountsConfig from "components/Invoices/Invoice/DiscountsConfig"
import ModalSearchParts from "components/Facturas/modals/modalSearchPart"
import { useTaxes } from "components/ProfileRefinament/Taxes/hooks/useTaxes"
import ProjectContext from "context/projects/projectContext"

const INITIAL_CONCEPT = {
  description: "",
  quantity: 1,
  price: 0,
  taxes: [],
  partidaId: undefined,
  discount: 0,
}

const PurchaseOrderConcepts = ({
  purchaseOrderData,
  setPurchaseOrderData,
  hasValidationErrors,
  readOnly = false,
}) => {
  const { user } = useContext(AuthContext)
  const organizationId = user["custom:organizationId"]
  const { projects = [] } = useContext(ProjectContext)

  const projectInfo = purchaseOrderData.projectId
    ? projects.find(p => p.projectId === purchaseOrderData.projectId)
    : null

  const [openSearchModal, setOpenSearchModal] = useState(false)
  const [selectedParts, setSelectedParts] = useState([])

  const [showProductDiscount, setShowProductDiscount] = useState(false)
  const [showGlobalDiscount, setShowGlobalDiscount] = useState(
    (purchaseOrderData.discount || 0) > 0,
  )
  const [globalDiscountValue, setGlobalDiscountValue] = useState(
    purchaseOrderData.discount || 0,
  )

  useEffect(() => {
    const hasProductDiscount = (
      purchaseOrderData.purchaseOrderConcepts || []
    ).some(c => (c.discount || 0) > 0)
    if (hasProductDiscount) {
      setShowProductDiscount(true)
    }
  }, [purchaseOrderData.purchaseOrderConcepts])

  useEffect(() => {
    if ((purchaseOrderData.discount || 0) > 0) {
      setShowGlobalDiscount(true)
      setGlobalDiscountValue(purchaseOrderData.discount)
    }
  }, [purchaseOrderData.discount])

  const concepts = readOnly
    ? Array.isArray(purchaseOrderData.purchaseOrderConcepts)
      ? [...purchaseOrderData.purchaseOrderConcepts].sort(
          (a, b) => (a.position ?? 0) - (b.position ?? 0),
        )
      : []
    : purchaseOrderData.purchaseOrderConcepts &&
        purchaseOrderData.purchaseOrderConcepts.length > 0
      ? [...purchaseOrderData.purchaseOrderConcepts].sort(
          (a, b) => (a.position ?? 0) - (b.position ?? 0),
        )
      : [INITIAL_CONCEPT]

  useEffect(() => {
    if (readOnly) return
    if (
      !purchaseOrderData.purchaseOrderConcepts ||
      purchaseOrderData.purchaseOrderConcepts.length === 0
    ) {
      setPurchaseOrderData(prev => ({
        ...prev,
        purchaseOrderConcepts: [INITIAL_CONCEPT],
      }))
    }
  }, [purchaseOrderData.purchaseOrderConcepts, setPurchaseOrderData, readOnly])

  const handleToggleProductDiscount = checked => {
    if (readOnly) return
    setShowProductDiscount(checked)
    if (!checked) {
      const updatedConcepts = concepts.map(c => ({ ...c, discount: 0 }))
      setPurchaseOrderData(prev => ({
        ...prev,
        purchaseOrderConcepts: updatedConcepts,
      }))
    }
  }

  const handleToggleGlobalDiscount = checked => {
    if (readOnly) return
    setShowGlobalDiscount(checked)
    if (!checked) {
      setGlobalDiscountValue(0)
      setPurchaseOrderData(prev => ({ ...prev, discount: 0 }))
    }
  }

  const handleGlobalDiscountChange = value => {
    if (readOnly) return
    setGlobalDiscountValue(value)
    setPurchaseOrderData(prev => ({ ...prev, discount: value }))
  }

  const handleAddFromParts = () => {
    if (readOnly) return
    if (selectedParts.length > 0) {
      setPurchaseOrderData(prev => {
        const currentConcepts = prev.purchaseOrderConcepts || []
        const isFirstConceptEmpty =
          currentConcepts.length === 1 &&
          (!currentConcepts[0].description ||
            currentConcepts[0].description.trim() === "") &&
          (currentConcepts[0].price === 0 || !currentConcepts[0].price)
        const safeCurrentConcepts = currentConcepts.map(c => ({
          ...c,
          ...(c.version !== undefined ? { version: c.version } : {}),
        }))

        const maxPosition =
          safeCurrentConcepts.length > 0
            ? Math.max(...safeCurrentConcepts.map(c => c.position ?? 0))
            : 0

        const newConcepts = selectedParts.map((partida, idx) => ({
          description: partida.name || partida.description || "",
          quantity: partida.quantity || 0,
          maxQuantity: partida.quantity || 0,
          price: partida.price,
          taxes: [],
          partidaId: partida.partidaId,
          discount: 0,
          position: maxPosition + idx + 1,
        }))
        return {
          ...prev,
          purchaseOrderConcepts: isFirstConceptEmpty
            ? newConcepts
            : [...safeCurrentConcepts, ...newConcepts],
        }
      })
    }
    setSelectedParts([])
    setOpenSearchModal(false)
  }

  const handleConceptChange = (idx, field, value) => {
    if (readOnly) return
    const updatedConcepts = concepts.map((c, i) => {
      if (i === idx) {
        return {
          ...c,
          [field]: value,
          ...(c.version !== undefined ? { version: c.version } : {}),
        }
      }
      return {
        ...c,
        ...(c.version !== undefined ? { version: c.version } : {}),
      }
    })
    setPurchaseOrderData(prev => ({
      ...prev,
      purchaseOrderConcepts: updatedConcepts,
    }))
  }

  const handleTaxesChange = (idx, taxes) => {
    if (readOnly) return
    const updatedConcepts = concepts.map((c, i) => {
      if (i === idx) {
        return {
          ...c,
          taxes,
          ...(c.version !== undefined ? { version: c.version } : {}),
        }
      }
      return {
        ...c,
        ...(c.version !== undefined ? { version: c.version } : {}),
      }
    })
    setPurchaseOrderData(prev => ({
      ...prev,
      purchaseOrderConcepts: updatedConcepts,
    }))
  }

  const handleAddConcept = () => {
    if (readOnly) return
    setPurchaseOrderData(prev => {
      const currentConcepts = prev.purchaseOrderConcepts || []
      const safeCurrentConcepts = currentConcepts.map(c => ({
        ...c,
        ...(c.version !== undefined ? { version: c.version } : {}),
      }))
      const newConcept = {
        ...INITIAL_CONCEPT,
        position: safeCurrentConcepts.length,
      }
      return {
        ...prev,
        purchaseOrderConcepts: [...safeCurrentConcepts, newConcept],
      }
    })
  }

  const handleDeleteConcept = idx => {
    if (readOnly) return
    if (concepts.length === 1) return
    setPurchaseOrderData(prev => {
      const filtered = concepts
        .map(c => ({
          ...c,
          ...(c.version !== undefined ? { version: c.version } : {}),
        }))
        .filter((_, i) => i !== idx)
      const reassigned = filtered.map((c, i) => ({ ...c, position: i }))
      return {
        ...prev,
        purchaseOrderConcepts: reassigned,
      }
    })
  }

  const { data: taxes = [] } = useTaxes(organizationId, !!organizationId)

  const taxOptions = taxes.reduce((acc, t) => {
    if (t.active) acc.push(t)
    return acc
  }, [])

  const handleSetTotals = ({ subtotal, tax, retention, total }) => {
    if (readOnly) return
    setPurchaseOrderData(prev => {
      if (
        prev.subtotal !== subtotal ||
        prev.tax !== tax ||
        prev.retention !== retention ||
        prev.total !== total
      ) {
        return { ...prev, subtotal, tax, retention, total }
      }
      return prev
    })
  }

  return (
    <>
      <Paper elevation={0} sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {i18n.t("invoices.concepts")}
        </Typography>
        {!readOnly && projectInfo && (
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={() => setOpenSearchModal(true)}
            >
              Buscar partida
            </Button>
          </Box>
        )}

        {concepts.map((concept, idx) => (
          <InvoiceConceptRow
            key={idx}
            concept={concept}
            idx={idx}
            onChange={handleConceptChange}
            onTaxesChange={handleTaxesChange}
            onDelete={handleDeleteConcept}
            disableDelete={concepts.length === 1}
            taxOptions={taxOptions}
            hasValidationErrors={hasValidationErrors}
            showProductDiscount={showProductDiscount}
            issued={readOnly}
          />
        ))}
        {!readOnly && (
          <Box sx={{ mt: 2 }}>
            <Button onClick={handleAddConcept}>
              + {i18n.t("invoices.newConcept")}
            </Button>
          </Box>
        )}
        <Grid container justifyContent="space-between" sx={{ mt: 4 }}>
          <DiscountsConfig
            showProductDiscount={showProductDiscount}
            onToggleProductDiscount={handleToggleProductDiscount}
            showGlobalDiscount={showGlobalDiscount}
            onToggleGlobalDiscount={handleToggleGlobalDiscount}
            globalDiscountValue={globalDiscountValue}
            setGlobalDiscountValue={handleGlobalDiscountChange}
            issued={readOnly}
            disableDiscountToggles
          />
          <InvoiceTotals
            concepts={concepts}
            setTotals={handleSetTotals}
            globalDiscount={globalDiscountValue}
          />
        </Grid>
      </Paper>

      {openSearchModal && projectInfo && (
        <ModalSearchParts
          open={openSearchModal}
          setOpen={setOpenSearchModal}
          projectInfo={projectInfo}
          selectedParts={selectedParts}
          setSelectedParts={setSelectedParts}
          handleAddFromParts={handleAddFromParts}
          priceField="price"
        />
      )}
    </>
  )
}

PurchaseOrderConcepts.propTypes = {
  purchaseOrderData: PropTypes.shape({
    purchaseOrderConcepts: PropTypes.array,
    projectId: PropTypes.string,
    discount: PropTypes.number,
  }).isRequired,
  setPurchaseOrderData: PropTypes.func.isRequired,
  hasValidationErrors: PropTypes.bool,
  readOnly: PropTypes.bool,
}

export default PurchaseOrderConcepts
